// I dati della Dashboard che non arrivano dallo stream: progetti, attività, pops, consumi per
// giorno, provider, git, aggiornamenti, e i lavori del piano delle sessioni recenti di ogni
// progetto. Si rileggono ogni minuto; un errore di una lettura non spegne le altre.
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import type {
  AgentProfile,
  ConnectedProvider,
  GitRegistry,
  Project,
  Routine,
  UpdatesView,
  Usage,
} from '../../lib/api';
import type { MissionTask, Session } from '../../types';

export interface DashboardData {
  projects: Project[] | null;
  routines: Routine[] | null;
  profiles: AgentProfile[] | null;
  usage: Usage | null;
  providers: ConnectedProvider[] | null;
  git: GitRegistry[] | null;
  updates: UpdatesView | null;
  tasks: Record<string, MissionTask[]>;
}

const EMPTY: DashboardData = {
  projects: null,
  routines: null,
  profiles: null,
  usage: null,
  providers: null,
  git: null,
  updates: null,
  tasks: {},
};
const EVERY_MS = 60_000;
const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** L'ultima sessione di ogni progetto: è quella di cui si mostrano piano e team. */
export function lastByProject(sessions: Session[]): Map<string, Session> {
  const out = new Map<string, Session>();
  for (const s of sessions) {
    if (!s.project) continue;
    const seen = out.get(s.project);
    if (!seen || s.updatedAt > seen.updatedAt) out.set(s.project, s);
  }
  return out;
}

export function useDashboard(sessions: Session[]): DashboardData {
  const [data, setData] = useState<DashboardData>(EMPTY);
  const watched = useMemo(() => {
    const open = sessions.filter((s) => !['completed', 'failed', 'cancelled'].includes(s.state));
    const last = [...lastByProject(sessions).values()];
    return [...new Set([...open, ...last].map((s) => s.id))].sort().join(',');
  }, [sessions]);

  useEffect(() => {
    let alive = true;
    const set = <K extends keyof DashboardData>(k: K, v: DashboardData[K]) => {
      if (alive) setData((d) => ({ ...d, [k]: v }));
    };
    const load = () => {
      void api.projects().then(
        (r) => set('projects', r.projects),
        () => undefined,
      );
      void api.routines().then(
        (r) => set('routines', r),
        () => undefined,
      );
      void api.profiles().then(
        (r) => set('profiles', r),
        () => undefined,
      );
      void api.usage(30, ZONE).then(
        (r) => set('usage', r),
        () => undefined,
      );
      void api.providers().then(
        (r) => set('providers', r.providers),
        () => undefined,
      );
      void api.gitRegistries().then(
        (r) => set('git', r.registries),
        () => undefined,
      );
      void api.updates().then(
        (r) => set('updates', r),
        () => undefined,
      );
    };
    load();
    const t = window.setInterval(load, EVERY_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const ids = watched ? watched.split(',') : [];
    const load = () =>
      void Promise.all(
        ids.map((id) =>
          api.tasks(id).then(
            (t) => [id, t] as const,
            () => [id, [] as MissionTask[]] as const,
          ),
        ),
      ).then((pairs) => {
        if (alive) setData((d) => ({ ...d, tasks: Object.fromEntries(pairs) }));
      });
    load();
    const t = window.setInterval(load, EVERY_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [watched]);

  return data;
}
